import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  House, Phone, Envelope, ShoppingCart, User, Heart, MagnifyingGlass,
  FacebookLogo, InstagramLogo, TiktokLogo, Tag, List as ListIcon, X,
  Truck, MapPin, CalendarCheck, Star, ShieldCheck, SignOut, DownloadSimple
} from '@phosphor-icons/react';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from '../../components/NotificationBell';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_STORE_SLUG = "magere-viandes";

const CATEGORY_IMG_MAP = {
  'Boeuf': 'https://images.unsplash.com/photo-1733700469173-15d46efc2c09?w=120&h=120&fit=crop&crop=center',
  'Agneau': 'https://images.pexels.com/photos/31732111/pexels-photo-31732111.jpeg?auto=compress&w=120&h=120&fit=crop',
  'Poulet': 'https://images.unsplash.com/photo-1772957041453-e0eb938535a8?w=120&h=120&fit=crop&crop=center',
  'Porc': 'https://images.pexels.com/photos/7333264/pexels-photo-7333264.jpeg?auto=compress&w=120&h=120&fit=crop',
  'Charcuterie': 'https://images.unsplash.com/photo-1614473491229-17bf47b9e487?w=120&h=120&fit=crop&crop=center',
  'Braseros': 'https://images.pexels.com/photos/36318468/pexels-photo-36318468.jpeg?auto=compress&w=120&h=120&fit=crop',
  'Canard': 'https://images.pexels.com/photos/24038062/pexels-photo-24038062.jpeg?auto=compress&w=120&h=120&fit=crop',
  'Kamados': 'https://images.unsplash.com/photo-1770318724110-c9d426f2125e?w=120&h=120&fit=crop&crop=center',
  'Accessoires': 'https://images.unsplash.com/photo-1560614382-33bd4daa1b9e?w=120&h=120&fit=crop&crop=center',
};

import { buildCategoryTree, getCategoryImage } from './components/shopUtils';

/* ═══════════ SEARCH BAR (isolated to prevent focus loss) ═══════════ */
const MobileInstallRow = ({ colors }) => {
  const [canInstall, setCanInstall] = React.useState(typeof window !== 'undefined' && !!window.__pwaPrompt);
  const [showIos, setShowIos] = React.useState(false);
  React.useEffect(() => {
    const h = () => setCanInstall(true);
    window.addEventListener('pwa-prompt-ready', h);
    return () => window.removeEventListener('pwa-prompt-ready', h);
  }, []);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  if (isStandalone || (!canInstall && !isIos)) return null;
  const install = async () => {
    if (window.__pwaPrompt) {
      window.__pwaPrompt.prompt();
      await window.__pwaPrompt.userChoice;
      window.__pwaPrompt = null;
      setCanInstall(false);
    } else if (isIos) {
      setShowIos(true);
    }
  };
  return (
    <>
      <button onClick={install} className="flex items-center gap-4 w-full py-3 text-[16px] text-white/90 hover:text-white font-medium" data-testid="mobile-install-app">
        <span style={{ color: colors.primary }}><DownloadSimple size={20} weight="fill" /></span>Installer l'application
      </button>
      {showIos && (
        <div className="fixed inset-0 z-[300] flex items-end justify-center bg-black/60 p-3" onClick={() => setShowIos(false)} data-testid="ios-install-hint">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-2 text-slate-800">Installer sur iPhone / iPad</h3>
            <ol className="text-sm space-y-2 text-slate-700 list-decimal ml-5">
              <li>Ouvrez ce site dans <b>Safari</b>.</li>
              <li>Touchez l'icône <b>Partager</b> (carré avec flèche).</li>
              <li>Choisissez <b>« Sur l'écran d'accueil »</b> puis <b>Ajouter</b>.</li>
            </ol>
            <button className="w-full mt-4 py-2.5 rounded-full text-white font-semibold" style={{ backgroundColor: colors.primary }} onClick={() => setShowIos(false)}>Compris</button>
          </div>
        </div>
      )}
    </>
  );
};

const HeaderSearchBar = React.memo(({ storeSlug, colors, onSearchSubmit, standalone = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API_URL}/api/s/${storeSlug}/products?search=${encodeURIComponent(query)}&limit=6`)
        .then(r => {
          const items = r.data?.products || r.data || [];
          setResults(items.slice(0, 6));
        }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query, storeSlug]);

  // Lock body scroll on mobile when open
  useEffect(() => {
    if (open && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const goToProduct = (p) => {
    setOpen(false); setQuery(''); setResults([]);
    navigate(standalone ? `/product/${p.slug}` : `/store/${storeSlug}/product/${p.slug}`);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-white/60 hover:text-white transition-colors" data-testid="search-toggle">
        <MagnifyingGlass size={24} />
      </button>
    );
  }

  return (
    <>
      {/* Desktop: inline search */}
      <div className="relative hidden md:block">
        <div className="flex items-center gap-2 bg-white/15 rounded-full px-3 py-1.5">
          <MagnifyingGlass size={18} className="text-white/60" />
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } }}
            placeholder="Rechercher..."
            className="bg-transparent text-white text-[14px] w-[180px] outline-none placeholder:text-white/40"
            data-testid="search-input"
          />
          <button onClick={() => { setOpen(false); setQuery(''); setResults([]); }} className="text-white/40 hover:text-white">
            <X size={16} />
          </button>
        </div>
        {results.length > 0 && (
          <div className="fixed top-[60px] right-6 w-[320px] bg-white rounded-xl shadow-2xl border z-[9999] max-h-[400px] overflow-y-auto">
            {results.map(p => (
              <button key={p.id} onClick={() => goToProduct(p)}
                className="flex items-center gap-3 w-full px-4 py-3 hover:bg-slate-50 text-left border-b last:border-0">
                {(p.featured_image || p.images?.[0]) && <img src={p.featured_image || p.images[0]} alt="" className="w-10 h-10 rounded object-cover" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                  <p className="text-xs font-bold" style={{ color: colors.primary }}>{parseFloat(p.price || 0).toFixed(2)} EUR</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Mobile: fullscreen overlay search */}
      <div className="md:hidden fixed inset-0 z-[200] flex flex-col" data-testid="mobile-search-overlay">
        {/* Dark backdrop */}
        <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => { setOpen(false); setQuery(''); setResults([]); }} />

        {/* Search panel */}
        <div className="relative z-10 mx-4 mt-[60px]">
          {/* Search input */}
          <div className="flex items-center gap-3 bg-white rounded-2xl px-4 py-3 shadow-2xl">
            <MagnifyingGlass size={20} className="text-slate-400 flex-shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } }}
              placeholder="Rechercher un produit..."
              className="flex-1 text-[16px] outline-none bg-transparent text-slate-800 placeholder:text-slate-300"
              data-testid="search-input-mobile"
            />
            <button onClick={() => { setOpen(false); setQuery(''); setResults([]); }}
              className="w-[32px] h-[32px] rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 flex-shrink-0">
              <X size={16} weight="bold" />
            </button>
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="mt-2 bg-white rounded-2xl shadow-2xl max-h-[60vh] overflow-y-auto">
              {results.map(p => (
                <button key={p.id} onClick={() => goToProduct(p)}
                  className="flex items-center gap-3 w-full px-4 py-3 hover:bg-slate-50 text-left border-b border-slate-50 last:border-0">
                  {(p.featured_image || p.images?.[0])
                    ? <img src={p.featured_image || p.images[0]} alt="" className="w-[48px] h-[48px] rounded-xl object-cover flex-shrink-0" />
                    : <div className="w-[48px] h-[48px] rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0 text-[14px] font-bold text-slate-300">{p.name?.[0]}</div>
                  }
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-semibold text-slate-800 truncate">{p.name}</p>
                    {p.category && <p className="text-[11px] text-slate-400 uppercase tracking-wider">{p.category}</p>}
                    <p className="text-[14px] font-bold mt-0.5" style={{ color: colors.primary }}>{parseFloat(p.price || 0).toFixed(2)} €</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Empty state */}
          {query.length >= 2 && results.length === 0 && (
            <div className="mt-2 bg-white rounded-2xl shadow-2xl px-4 py-8 text-center">
              <MagnifyingGlass size={32} className="mx-auto text-slate-300 mb-2" />
              <p className="text-[14px] text-slate-400">Aucun résultat pour "{query}"</p>
            </div>
          )}
        </div>
      </div>

      {/* Keep the toggle button visible on mobile (for z-index reference) */}
      <button className="md:hidden text-white/60" style={{ visibility: 'hidden', width: 24, height: 24 }}>
        <MagnifyingGlass size={24} />
      </button>
    </>
  );
});

/* ═══════════ MAGERE HEADER ═══════════ */
export const MagereHeader = ({ store, activePage = '', categories = [], onBoutiqueClick, onCartClick, onHomeClick, standalone = false }) => {
  const { storeSlug: slugParam } = useParams();
  const storeSlug = standalone ? DEFAULT_STORE_SLUG : (slugParam || store?.slug);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { itemCount, setIsOpen, setStoreSlug } = useCart();
  const { wishlistCount, fetchWishlistBySlug } = useWishlist();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Helper for standalone links
  const getLink = (path) => standalone ? path : `/store/${storeSlug}${path}`;

  useEffect(() => { if (storeSlug) setStoreSlug(storeSlug); }, [storeSlug, setStoreSlug]);
  useEffect(() => { if (storeSlug) fetchWishlistBySlug(storeSlug); }, [storeSlug, fetchWishlistBySlug]);

  const handleLogout = async () => {
    await logout();
    navigate(getLink('/'));
  };

  if (!store) return null;

  const colors = store?.theme_config?.colors || { primary: '#c2185b' };
  const id = store?.identity || {};
  const co = store?.company || {};
  const logoUrl = id.logo_url || id.logo_light || store?.theme_config?.logo_url;
  const phone = co.contact_phone || id.contact_phone || '';
  const email = co.contact_email || id.contact_email || '';

  const parentCats = (categories || []).filter(c => !c.parent_id);

  const handleCartClick = () => {
    if (onCartClick) onCartClick();
    else setIsOpen(true);
  };

  const handleBoutiqueClick = () => {
    if (onBoutiqueClick) onBoutiqueClick();
    else navigate(standalone ? '/' : `/store/${storeSlug}`);
  };

  const handleHomeClick = () => {
    if (onHomeClick) onHomeClick();
    else navigate(standalone ? '/' : `/store/${storeSlug}`);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50" data-testid="magere-header" style={{ fontFamily: "'Jost', sans-serif" }}>
        <div className="backdrop-blur-md" style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}>
          {/* ── DESKTOP ──
              Robust 3-column GRID layout: left | logo-slot | right.
              The center grid column is the FIXED logo lane → physically impossible
              for the side columns to overlap it. Side columns clip overflow. */}
          <div className="hidden md:grid grid-cols-[1fr_160px_1fr] max-w-[1300px] mx-auto px-8 items-center h-[56px]">
            <nav className="flex items-center gap-0 text-[16px] min-w-0 overflow-hidden justify-start">
              <button onClick={handleHomeClick} className="text-white/80 hover:text-white transition-colors px-2 flex-shrink-0" data-testid="nav-home">
                <House size={22} weight="fill" style={{ color: colors.primary }} />
              </button>
              <span style={{ color: colors.primary }} className="text-[16px] select-none flex-shrink-0">|</span>
              {[
                { label: 'Le magasin', to: getLink('/page/le-magasin'), key: 'le-magasin' },
                { label: 'Nos conseils', to: getLink('/blog'), key: 'blog' },
                { label: 'Nous contacter', to: getLink('/page/contact'), key: 'contact' },
              ].map((item, i) => (
                <React.Fragment key={item.key}>
                  <Link to={item.to}
                    className={`px-3 h-[56px] flex items-center font-medium text-[16px] whitespace-nowrap flex-shrink-0 transition-colors ${activePage === item.key ? 'text-white' : 'text-white/80 hover:text-white'}`}
                    style={activePage === item.key ? { color: colors.primary, fontWeight: 700 } : {}}
                    data-testid={`nav-${item.key}`}>
                    {item.label}
                  </Link>
                  {i < 2 && <span style={{ color: colors.primary }} className="text-[16px] select-none flex-shrink-0">|</span>}
                </React.Fragment>
              ))}
            </nav>

            {/* Center LOGO slot - actual grid column */}
            <div className="relative h-full">
              <Link to={standalone ? '/' : `/store/${storeSlug}`} onClick={handleHomeClick} data-testid="store-logo" className="absolute top-1 left-1/2 -translate-x-1/2 z-[5]">
                {logoUrl
                  ? <div className="h-[110px] w-[110px] rounded-full bg-white shadow-xl border-[3px] border-white ring-1 ring-black/5 flex items-center justify-center overflow-hidden" style={{ aspectRatio: '1 / 1' }}>
                      <img src={logoUrl} alt={store.name} className="w-[88%] h-[88%] object-contain" />
                    </div>
                  : <div className="h-[110px] w-[110px] rounded-full bg-white shadow-xl border-[3px] border-white ring-1 ring-black/5 flex items-center justify-center" style={{ aspectRatio: '1 / 1' }}>
                      <span className="text-[12px] font-black text-center leading-tight" style={{ color: colors.primary }}>{store.name}</span>
                    </div>}
              </Link>
            </div>

            <div className="flex items-center gap-0 text-[17px] min-w-0 overflow-hidden justify-end pr-6">
              <button onClick={handleBoutiqueClick} className="flex items-center gap-1.5 text-white/80 hover:text-white transition-colors px-2 h-[56px] whitespace-nowrap" data-testid="nav-boutique">
                <ShoppingCart size={19} weight="fill" /><span className="hidden lg:inline font-medium text-[17px]">Boutique en ligne</span>
              </button>
              <span style={{ color: colors.primary }} className="hidden lg:inline text-[17px] select-none px-1">|</span>
              {phone && (
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="hidden md:flex items-center text-white/80 hover:text-white transition-colors px-2 h-[56px] whitespace-nowrap" title={`Tel : ${phone}`} aria-label={`Téléphone ${phone}`} data-testid="header-phone-link">
                  <Phone size={22} weight="fill" style={{ color: colors.primary }} />
                </a>
              )}
              <div className="flex items-center gap-2.5 ml-2">
                <HeaderSearchBar storeSlug={storeSlug} colors={colors} standalone={standalone} />
                {user && <NotificationBell primaryColor={colors.primary} standalone={standalone} />}
                {user ? (
                  <>
                    <Link to={getLink('/account')} className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors text-[13px]" data-testid="account-btn">
                      <User size={18} weight="fill" style={{ color: colors.primary }} />
                      <span className="hidden lg:inline">{(user.name || user.email || '').split(' ')[0]}</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="text-white/60 hover:text-white transition-colors"
                      data-testid="logout-btn-desktop"
                      title="Se déconnecter"
                    >
                      <SignOut size={20} />
                    </button>
                  </>
                ) : (
                  <Link to={getLink('/account')} className="text-white/60 hover:text-white transition-colors" data-testid="account-btn"><User size={24} /></Link>
                )}
                <button className="relative text-white/60 hover:text-white transition-colors" onClick={() => navigate(getLink('/account/wishlist'))} data-testid="favorites-btn">
                  <Heart size={24} />
                  <span className="absolute -top-1 -right-2 w-[16px] h-[16px] text-white text-[9px] font-bold rounded-full flex items-center justify-center" style={{ backgroundColor: colors.primary }}>{wishlistCount}</span>
                </button>
                <button className="relative text-white/60 hover:text-white transition-colors" onClick={handleCartClick} data-testid="cart-btn">
                  <ShoppingCart size={24} />
                  <span className="absolute -top-1 -right-2 w-[16px] h-[16px] text-white text-[9px] font-bold rounded-full flex items-center justify-center" style={{ backgroundColor: colors.primary }}>{itemCount}</span>
                </button>
              </div>
            </div>
          </div>

          {/* ── MOBILE ── */}
          <div className="flex md:hidden items-center justify-between px-4 h-[56px] relative">
            <button onClick={() => setMobileMenuOpen(true)} className="text-white p-1 z-10" data-testid="mobile-menu-btn">
              <ListIcon size={26} weight="bold" />
            </button>
            <Link to={standalone ? '/' : `/store/${storeSlug}`} onClick={handleHomeClick} className="absolute left-1/2 -translate-x-1/2 top-0 z-20">
              {logoUrl
                ? <img src={logoUrl} alt={store.name} className="h-[80px] w-[80px] object-contain rounded-full bg-white shadow-lg mt-[-4px] border-[3px] border-white" />
                : <div className="h-[80px] w-[80px] rounded-full bg-white shadow-lg mt-[-4px] border-[3px] border-white flex items-center justify-center">
                    <span className="text-[10px] font-black text-center leading-tight" style={{ color: colors.primary }}>{store.name}</span>
                  </div>}
            </Link>
            <div className="flex items-center gap-3 z-10">
              {user && <NotificationBell primaryColor={colors.primary} standalone={standalone} />}
              <HeaderSearchBar storeSlug={storeSlug} colors={colors} standalone={standalone} />
              <button className="relative text-white/70" onClick={handleCartClick} data-testid="cart-btn-mobile">
                <ShoppingCart size={22} weight="bold" />
                {itemCount > 0 && <span className="absolute -top-1 -right-2 w-[16px] h-[16px] text-white text-[9px] font-bold rounded-full flex items-center justify-center" style={{ backgroundColor: colors.primary }}>{itemCount}</span>}
              </button>
            </div>
          </div>
          {/* Connected user banner */}
          {user && (
            <div className="md:hidden flex items-center justify-center gap-1.5 py-1 px-4 text-[11px] text-white/70" style={{ backgroundColor: 'rgba(0,0,0,0.15)' }} data-testid="user-banner-mobile">
              <User size={12} weight="fill" style={{ color: colors.primary }} />
              <span>Connecté en tant que <strong className="text-white/90">{(user.name || user.email || '').split(' ')[0]}</strong></span>
              <Link to={getLink('/account')} className="ml-1 underline text-white/50 hover:text-white/80">Mon compte</Link>
              <span className="text-white/30">·</span>
              <button
                onClick={handleLogout}
                className="underline text-white/50 hover:text-white/80"
                data-testid="logout-btn-mobile"
              >
                Déconnexion
              </button>
            </div>
          )}
        </div>

        {/* ── MOBILE MEGA MENU ── */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-[100] md:hidden" data-testid="mobile-menu">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-[320px] overflow-y-auto" style={{ backgroundColor: '#1a1a1a' }}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                {logoUrl
                  ? <img src={logoUrl} alt={store.name} className="h-[50px] w-[50px] object-contain rounded-full bg-white" />
                  : <span className="text-[16px] font-black text-white">{store.name}</span>}
                <button onClick={() => setMobileMenuOpen(false)} className="text-white/60 hover:text-white p-1"><X size={24} weight="bold" /></button>
              </div>
              <nav className="px-5 py-4 border-b border-white/10">
                <button onClick={() => { handleHomeClick(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-4 w-full py-3.5 text-[16px] text-white/90 hover:text-white font-medium">
                  <span style={{ color: colors.primary }}><House size={20} weight="fill" /></span>Accueil
                </button>
                <button onClick={() => { handleBoutiqueClick(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-4 w-full py-3.5 text-[16px] text-white/90 hover:text-white font-medium">
                  <span style={{ color: colors.primary }}><ShoppingCart size={20} weight="fill" /></span>Boutique en ligne
                </button>
                {[
                  { label: 'Nos conseils', to: getLink('/blog'), icon: <Tag size={20} weight="fill" /> },
                  { label: 'Nous contacter', to: getLink('/page/contact'), icon: <Envelope size={20} weight="fill" /> },
                ].map(item => (
                  <Link key={item.label} to={item.to} onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-4 w-full py-3.5 text-[16px] text-white/90 hover:text-white font-medium">
                    <span style={{ color: colors.primary }}>{item.icon}</span>{item.label}
                  </Link>
                ))}
              </nav>
              {parentCats.length > 0 && (() => {
                const tree = buildCategoryTree(parentCats, []);
                const getCatImg = (c) => getCategoryImage(c) || CATEGORY_IMG_MAP[c.name] || Object.entries(CATEGORY_IMG_MAP).find(([k]) => c.name.toLowerCase().includes(k.toLowerCase()))?.[1] || null;
                return (
                <>
                <div className="px-5 py-4 border-b border-white/10">
                  <h4 className="text-[14px] font-bold uppercase tracking-wider mb-3" style={{ color: colors.primary }}>Nos univers</h4>
                  {tree.featured.map(c => {
                    const img = getCatImg(c);
                    return (
                    <Link key={c.slug} to={`${standalone ? '/' : `/store/${storeSlug}`}?cat=${encodeURIComponent(c.slug)}`} onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-4 w-full py-3 text-[17px] text-white/85 hover:text-white transition-colors font-medium">
                      {img ? <img src={img} alt="" className="w-[44px] h-[44px] rounded-full object-cover border-2 border-white/20" onError={e => { e.currentTarget.style.display = 'none'; }} />
                       : <span className="w-[44px] h-[44px] rounded-full bg-white/10 flex items-center justify-center text-[14px] font-bold" style={{ color: colors.primary }}>{c.name[0]}</span>}
                      {c.name}
                    </Link>
                    );
                  })}
                </div>
                <div className="px-5 py-4 border-b border-white/10">
                  <h4 className="text-[14px] font-bold uppercase tracking-wider mb-3" style={{ color: colors.primary }}>Nos viandes & produits</h4>
                  {tree.meats.map(({ parent: c, children }) => {
                    const img = getCatImg(c);
                    return (
                    <div key={c.slug}>
                      <Link to={`${standalone ? '/' : `/store/${storeSlug}`}?cat=${encodeURIComponent(c.slug)}`} onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-4 w-full py-3 text-[17px] text-white/85 hover:text-white transition-colors font-medium">
                        {img ? <img src={img} alt="" className="w-[44px] h-[44px] rounded-full object-cover border-2 border-white/20" onError={e => { e.currentTarget.style.display = 'none'; }} />
                         : <span className="w-[44px] h-[44px] rounded-full bg-white/10 flex items-center justify-center text-[14px] font-bold" style={{ color: colors.primary }}>{c.name[0]}</span>}
                        {c.name}
                      </Link>
                      {children.length > 0 && (
                        <div className="ml-14 border-l border-white/10 pl-3">
                          {children.map(ch => (
                            <Link key={ch.slug} to={`${standalone ? '/' : `/store/${storeSlug}`}?cat=${encodeURIComponent(ch.slug)}`} onClick={() => setMobileMenuOpen(false)}
                              className="block py-2 text-[14px] text-white/50 hover:text-white/80 transition-colors">
                              {ch.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
                </>
                );
              })()}
              <div className="px-5 py-4 border-b border-white/10">
                <Link to={getLink('/account')} onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-4 w-full py-3 text-[16px] text-white/90 hover:text-white font-medium">
                  <span style={{ color: colors.primary }}><User size={20} weight="fill" /></span>Mon compte
                </Link>
                <Link to={getLink('/account/wishlist')} onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-4 w-full py-3 text-[16px] text-white/90 hover:text-white font-medium">
                  <span style={{ color: colors.primary }}><Heart size={20} weight="fill" /></span>Mes favoris
                </Link>
                <MobileInstallRow colors={colors} />
              </div>
              <div className="px-5 py-4">
                {phone && <a href={`tel:${phone.replace(/\s/g, '')}`} className="flex items-center gap-3 py-2 text-[15px] text-white/80 hover:text-white">
                  <Phone size={18} weight="fill" style={{ color: colors.primary }} /> {phone}
                </a>}
                {email && <a href={`mailto:${email}`} className="flex items-center gap-3 py-2 text-[15px] text-white/80 hover:text-white">
                  <Envelope size={18} weight="fill" style={{ color: colors.primary }} /> {email}
                </a>}
                <div className="flex gap-3 mt-4">
                  {id.social_facebook && <a href={id.social_facebook} target="_blank" rel="noopener noreferrer"
                    className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-white" style={{ backgroundColor: '#1877F2' }}>
                    <FacebookLogo size={20} weight="fill" /></a>}
                  {id.social_instagram && <a href={id.social_instagram} target="_blank" rel="noopener noreferrer"
                    className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-white" style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}>
                    <InstagramLogo size={20} weight="fill" /></a>}
                  {id.social_tiktok && <a href={id.social_tiktok} target="_blank" rel="noopener noreferrer"
                    className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-white bg-black">
                    <TiktokLogo size={20} weight="fill" /></a>}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

/* ═══════════ MAGERE FOOTER ═══════════ */
export const MagereFooter = ({ store, pages = [], categories = [], standalone = false }) => {
  const { storeSlug: slugParam } = useParams();
  const storeSlug = standalone ? DEFAULT_STORE_SLUG : (slugParam || store?.slug);

  // Helper for standalone links
  const getLink = (path) => standalone ? path : `/store/${storeSlug}${path}`;

  // Fetch delivery info dynamically
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  useEffect(() => {
    if (!storeSlug) return;
    fetch(`${API_URL}/api/s/${storeSlug}/delivery-info`)
      .then(r => r.json())
      .then(d => setDeliveryInfo(d))
      .catch(() => {});
  }, [storeSlug]);

  if (!store) return null;

  const colors = store?.theme_config?.colors || { primary: '#c2185b' };
  const id = store?.identity || {};
  const co = store?.company || {};
  const logoUrl = id.logo_url || id.logo_light || store?.theme_config?.logo_url;
  const phone = co.contact_phone || id.contact_phone || '';
  const email = co.contact_email || id.contact_email || '';
  const companyName = co.company_name || id.company_name || store?.name || '';
  const fbUrl = co.social?.facebook || id.social_facebook || '';
  const igUrl = co.social?.instagram || id.social_instagram || '';
  const ttUrl = co.social?.tiktok || id.social_tiktok || '';
  const fbEmbedUrl = fbUrl ? `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(fbUrl)}&tabs=timeline&width=500&height=294&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=true&appId` : '';
  const parentCats = (categories || []).filter(c => !c.parent_id);

  return (
    <footer data-testid="store-footer" style={{ fontFamily: "'Jost', sans-serif" }}>
      <div style={{ backgroundColor: colors.primary, height: 6 }} />
      <div style={{ backgroundColor: '#000000' }}>
        <div className="max-w-[1300px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 md:gap-10">
            {/* Col 1: Logo + contact + socials */}
            <div className="lg:col-span-3">
              {logoUrl ? <img src={logoUrl} alt={store.name} className="h-[100px] mb-5 object-contain" />
               : <span className="text-2xl font-black text-white block mb-5">{store.name}</span>}
              <div className="space-y-2.5 text-[16px] text-white/80 mb-5">
                {phone && <a href={`tel:${phone.replace(/\s/g, '')}`} className="flex items-center gap-2.5 hover:text-white transition-colors">
                  <Phone size={15} weight="fill" style={{ color: colors.primary }} /> {phone}</a>}
                {email && <a href={`mailto:${email}`} className="flex items-center gap-2.5 hover:text-white transition-colors">
                  <Envelope size={15} weight="fill" style={{ color: colors.primary }} /> {email}</a>}
              </div>
              <p className="text-[15px] font-semibold mb-3" style={{ color: colors.primary }}>Suivez-Nous Sur</p>
              <div className="flex gap-2.5">
                {fbUrl && <a href={fbUrl} target="_blank" rel="noopener noreferrer"
                  className="w-[38px] h-[38px] rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity" style={{ backgroundColor: '#1877F2' }}>
                  <FacebookLogo size={20} weight="fill" /></a>}
                {igUrl && <a href={igUrl} target="_blank" rel="noopener noreferrer"
                  className="w-[38px] h-[38px] rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity" style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}>
                  <InstagramLogo size={20} weight="fill" /></a>}
                {ttUrl && <a href={ttUrl} target="_blank" rel="noopener noreferrer"
                  className="w-[38px] h-[38px] rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity" style={{ backgroundColor: '#000' }}>
                  <TiktokLogo size={20} weight="fill" /></a>}
              </div>
            </div>

            {/* Col 2: Menu */}
            <div className="lg:col-span-2">
              <h4 className="text-[18px] font-bold mb-5" style={{ color: colors.primary }}>Menu</h4>
              <ul className="space-y-0">
                {[{ label: 'Accueil', to: standalone ? '/' : `/store/${storeSlug}` }, 
                  { label: 'Le magasin', to: getLink('/page/le-magasin') },
                  { label: 'Nos conseils', to: getLink('/blog') }, 
                  { label: 'Nous contacter', to: getLink('/page/contact') }
                ].map(item => (
                  <li key={item.label} className="border-b border-white/10">
                    <Link to={item.to} className="py-3.5 text-[15px] text-white/80 hover:text-white block transition-colors">{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Col 3: Nos Produits */}
            <div className="lg:col-span-2">
              <h4 className="text-[18px] font-bold mb-5" style={{ color: colors.primary }}>Nos Produits</h4>
              {(() => {
                const tree = buildCategoryTree(parentCats, []);
                return (
                  <ul className="space-y-2.5">
                    {tree.featured.slice(0, 4).map(c => (
                      <li key={c.slug}>
                        <Link to={`${standalone ? '/' : `/store/${storeSlug}`}?cat=${encodeURIComponent(c.slug)}`} className="flex items-center gap-2.5 text-[15px] text-slate-300 hover:text-white transition-colors whitespace-nowrap">
                          <span className="w-[8px] h-[8px] rounded-full flex-shrink-0" style={{ backgroundColor: colors.primary }} />{c.name}
                        </Link>
                      </li>
                    ))}
                    {tree.meats.slice(0, 6).map(({ parent: c }) => (
                      <li key={c.slug}>
                        <Link to={`${standalone ? '/' : `/store/${storeSlug}`}?cat=${encodeURIComponent(c.slug)}`} className="flex items-center gap-2.5 text-[15px] text-slate-300 hover:text-white transition-colors whitespace-nowrap">
                          <span className="w-[8px] h-[8px] rounded-full flex-shrink-0" style={{ backgroundColor: colors.primary }} />{c.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>

            {/* Col 4: Facebook Widget */}
            {fbUrl && (
              <div className="lg:col-span-5">
                <h4 className="text-[18px] font-bold mb-5" style={{ color: colors.primary }}>Suivez-nous</h4>
                <a href={fbUrl} target="_blank" rel="noopener noreferrer"
                  className="block rounded-xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/20 transition-all group p-5"
                  data-testid="fb-widget">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-[56px] h-[56px] rounded-full flex items-center justify-center" style={{ backgroundColor: '#1877F2' }}>
                      <FacebookLogo size={30} weight="fill" className="text-white" />
                    </div>
                    <div>
                      <p className="text-[16px] font-bold text-white group-hover:underline">{companyName || 'Magère'}</p>
                      <p className="text-[13px] text-white/50">Facebook</p>
                    </div>
                  </div>
                  <p className="text-[14px] text-white/60 mb-4 leading-relaxed">Retrouvez nos dernières actualités, recettes et promotions sur notre page Facebook !</p>
                  <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-[13px] font-bold transition-all group-hover:brightness-110"
                    style={{ backgroundColor: '#1877F2' }}>
                    <FacebookLogo size={16} weight="fill" /> Voir notre page
                  </span>
                </a>
                {id.social_instagram && (
                  <a href={id.social_instagram} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 mt-3 px-5 py-3 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all group">
                    <InstagramLogo size={22} weight="fill" className="text-pink-400" />
                    <span className="text-[14px] text-white/70 group-hover:text-white font-medium">Suivez-nous sur Instagram</span>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Delivery marquee */}
        <div className="overflow-hidden border-t border-white/5" style={{ backgroundColor: colors.primary + '15' }} data-testid="delivery-marquee">
          <div className="py-2.5 whitespace-nowrap marquee-track">
            {[0, 1].map((rep) => (
              <span key={rep} className="inline-flex items-center gap-0 text-[13px] marquee-content" aria-hidden={rep === 1}>
                <span className="inline-flex items-center gap-2 mx-5">
                  <Truck size={14} weight="fill" style={{ color: colors.primary }} />
                  <span className="text-white font-bold">Mardi, Jeudi ou Samedi</span>
                  <span className="text-white/50">:</span>
                  <span className="text-white/80">Istres, Fos-sur-Mer, Port-de-Bouc, Martigues, Châteauneuf-les-Martigues, Sausset-les-Pins, Carry-le-Rouet, Ensuès-la-Redonne, Port-Saint-Louis-du-Rhône <span className="text-white/50">(seulement le jeudi)</span>, Marignane, Vitrolles, Les Pennes-Mirabeau, Rognac, Gignac-la-Nerthe</span>
                </span>
                <span className="text-white/20">|</span>
                <span className="inline-flex items-center gap-2 mx-5">
                  <Truck size={14} weight="fill" style={{ color: colors.primary }} />
                  <span className="text-white font-bold">Mercredi ou Vendredi</span>
                  <span className="text-white/50">:</span>
                  <span className="text-white/80">Istres, Saint-Chamas, Cornillon-Confoux, Miramas, Lançon, La Fare-les-Oliviers, Pélissanne, Saint-Martin-de-Crau, Berre-l'Étang, Velaux, Grans, Salon-de-Provence, Eyguières, Lamanon, Sénas, Mallemort, Orgon, Saint-Andiol, Cavaillon, Mérindol, Cheval-Blanc</span>
                </span>
                <span className="text-white/20">|</span>
                <span className="inline-flex items-center gap-2 mx-5">
                  <Star size={14} weight="fill" style={{ color: colors.primary }} />
                  <span className="text-white/80">Livraison <strong className="text-white">GRATUITE</strong> dès <strong style={{ color: colors.primary }}>100€</strong> d'achat</span>
                </span>
                <span className="text-white/20">|</span>
                <span className="inline-flex items-center gap-2 mx-5">
                  <span className="text-white/70">Livraison avant <strong className="text-white">12h</strong></span>
                  <span className="text-white/50">— 24h à 48h</span>
                </span>
                <span className="w-[40px] inline-block" />
              </span>
            ))}
          </div>
          <style>{`
            .marquee-track { display: flex; animation: marqueeLoop 45s linear infinite; }
            .marquee-content { display: inline-flex; flex-shrink: 0; }
            @keyframes marqueeLoop { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
          `}</style>
        </div>

        {/* Legal links + bottom bar */}
        <div className="border-t border-white/10">
          <div className="max-w-[1300px] mx-auto px-4 md:px-6 py-4">
            {pages.filter(p => p.is_published && !['home', 'blog'].includes(p.slug)).length > 0 && (
              <div className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 mb-3">
                {pages.filter(p => p.is_published && !['home', 'blog'].includes(p.slug)).map(pg => (
                  <Link key={pg.id} to={getLink(`/page/${pg.slug}`)} className="text-[13px] text-white/55 hover:text-white/80 transition-colors">{pg.title}</Link>
                ))}
              </div>
            )}
            <div className="flex flex-col items-center gap-3 mb-3">
              <span className="text-[13px] font-medium tracking-wide" style={{ color: colors.primary }}>Ouvert aux particuliers et aux professionnels</span>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
              <div className="flex flex-col items-center md:items-start gap-0.5">
                <span className="text-[12px] text-white/50 text-center md:text-left">{companyName} &copy; {new Date().getFullYear()} Tous droits reserves</span>
                {store?.last_deployed_at && (
                  <span className="text-[11px] text-white/30" data-testid="footer-last-deploy" title="Date du dernier déploiement du site">
                    Dernière mise à jour : {new Date(store.last_deployed_at).toLocaleString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {['CB', 'MasterCard', 'VISA', 'PayPal'].map(p => (
                  <div key={p} className="px-3 py-1.5 bg-white rounded text-[10px] font-bold text-slate-700">{p}</div>
                ))}
                <Link 
                  to={standalone ? '/admin/login' : `/store/${storeSlug}/admin`}
                  className="w-[32px] h-[32px] rounded-full flex items-center justify-center text-white/30 hover:text-white/60 hover:bg-white/10 transition-all ml-1"
                  title="Administration"
                  data-testid="admin-access-btn"
                >
                  <ShieldCheck size={16} weight="fill" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};


/* ═══════════ DEFAULT EXPORT: FULL LAYOUT WRAPPER ═══════════ */
const MagereSharedLayout = ({ children, storeSlug: propStoreSlug }) => {
  const { storeSlug: paramStoreSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug || DEFAULT_STORE_SLUG;
  const standalone = !!propStoreSlug || !paramStoreSlug;
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [categories, setCategories] = useState([]);
  const [pages, setPages] = useState([]);

  useEffect(() => {
    if (!storeSlug) return;
    axios.get(`${API_URL}/api/s/${storeSlug}`).then(r => setStore(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/s/${storeSlug}/categories`).then(r => setCategories(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/api/s/${storeSlug}/pages`).then(r => setPages(r.data || [])).catch(() => {});
  }, [storeSlug]);

  if (!store) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#c2185b' }} /></div>;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <MagereHeader
        store={store}
        categories={categories}
        onBoutiqueClick={() => navigate(standalone ? '/' : `/store/${storeSlug}`)}
        onCartClick={() => {}}
        onHomeClick={() => navigate(standalone ? '/' : `/store/${storeSlug}`)}
        standalone={standalone}
      />
      <main className="flex-1 pt-[56px]">{children}</main>
      <MagereFooter store={store} pages={pages} categories={categories} standalone={standalone} />
    </div>
  );
};

export default MagereSharedLayout;
